import React, { useEffect, useState } from 'react';
import {
  Stack,
  Grid,
  Card,
  CardHeader,
  Box,
  IconButton,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import ReactApexChart from 'react-apexcharts';
import useChart from '../../hooks/charts.hook';
import PLATFORMS_LIST, {
  getPlatformColorCode,
} from '../../config/platforms.config';
import { getDate, formatDate } from '../../utils/datetime.util';
import Channels from '../form-elements/channels.component';
import Datepicker from '../form-elements/datepicker.component';
import Iconify from '../common/iconify.component';
import { getReportsData } from '../../services/reports.service';
import WatchTimeType from '../form-elements/watch-time-type.component';
import Platforms from '../form-elements/platforms.component';

const WatchedSecondsChart = () => {
  const [filters, setFilters] = useState({
    channel: null,
    platform: null,
    startDate: getDate(-7),
    endDate: getDate(0),
    watchTimeType: 'MINUTES',
    reloadCounter: 0,
    loading: true,
  });
  const [seriesData, setSeriesData] = useState([]);
  const [xLabels, setXLabels] = useState([]);
  const [chartInstance, setChartInstance] = useState(null);
  const [selectedAllPlatforms, setSelectedAllPlatforms] = useState(null);
  const chartOptions = useChart({
    plotOptions: { bar: { columnWidth: '16%' } },
    fill: { type: seriesData.map((i) => i.fill) },
    labels: xLabels,
    xaxis: { type: 'date' },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (y) => {
          if (typeof y !== 'undefined') {
            return `${y.toFixed(1)} ${filters.watchTimeType.toLowerCase()}`;
          }
          return y;
        },
      },
    },
    yaxis: {
      labels: {
        formatter: (value) => value.toFixed(1),
      },
    },
    chart: {
      id: 'watched-seconds-chart',
      events: {
        mounted: (chartCtx) => {
          setChartInstance(chartCtx);
        },
      },
      toolbar: {
        show: false,
        tools: {
          zoom: false,
          zoomin: false,
          zoomout: false,
        },
      },
    },
  });

  const handleWatchTimeTypeChange = (v) => {
    setFilters({
      ...filters,
      watchTimeType: v.target.value,
      reloadCounter: filters.reloadCounter + 1,
      loading: true,
    });
  };
  const handleChannelChange = (v) => {
    setFilters({
      ...filters,
      channel: v,
      reloadCounter: filters.reloadCounter + 1,
      loading: true,
    });
  };
  const handlePlatformChange = (v) => {
    setFilters({
      ...filters,
      platform: v,
      reloadCounter: filters.reloadCounter + 1,
      loading: true,
    });
  };
  const handleStartDateChange = (v) => {
    setFilters({
      ...filters,
      startDate: v,
      reloadCounter: filters.reloadCounter + 1,
      loading: true,
    });
  };
  const handleEndDateChange = (v) => {
    setFilters({
      ...filters,
      endDate: v,
      reloadCounter: filters.reloadCounter + 1,
      loading: true,
    });
  };
  const handleRefreshData = () => {
    setFilters({
      ...filters,
      reloadCounter: filters.reloadCounter + 1,
      loading: true,
    });
  };

  useEffect(() => {
    const startDate = formatDate(filters.startDate, 'YYYY-MM-DD');
    const endDate = formatDate(filters.endDate, 'YYYY-MM-DD');

    const payload = {
      startDate,
      endDate,
      reportType: 'date_wise_per_channel_per_platform_total_seconds_watched',
      watchTimeType: filters.watchTimeType,
    };
    if (filters.channel) {
      payload.channels = filters.channel.map((item) => item.id);
    }
    if (filters.platform) {
      payload.platforms = filters.platform.map((item) => item.id);
    }

    getReportsData(payload)
      .then((res) => {
        const chartXLabels = [];
        const chartXLabelIndex = {};
        const chartPlatformLabels =
          !filters.channel || filters.channel.length <= 1
            ? PLATFORMS_LIST.filter((item) => item.id !== 'ADMIN').map(
                (item) => item.id
              )
            : [];
        const chartPlatformLabelIndex =
          !filters.channel || filters.channel.length <= 1
            ? PLATFORMS_LIST.filter((item) => item.id !== 'ADMIN').reduce(
                (acc, item, idx) => {
                  if (!acc[item.id]) {
                    acc[item.id] = idx;
                  }

                  return acc;
                },
                {}
              )
            : {};
        const chartSeriesData = [];

        // Prepare x label index and series data possible length
        res.data.rows.forEach((v) => {
          if (!chartXLabels.includes(v.watchDate)) {
            chartXLabels.push(v.watchDate);
            chartXLabelIndex[v.watchDate] = chartXLabels.length - 1;
          }
          if (
            (!filters.channel || filters.channel.length <= 1) &&
            !chartPlatformLabels.includes(v.platform)
          ) {
            chartPlatformLabels.push(v.platform);
            chartPlatformLabelIndex[v.platform] =
              chartPlatformLabels.length - 1;
          } else if (
            filters.channel?.length > 1 &&
            !chartPlatformLabels.includes(v.channel)
          ) {
            chartPlatformLabels.push(v.channel);
            chartPlatformLabelIndex[v.channel] = chartPlatformLabels.length - 1;
          }
        });

        // Prepare empty dataset
        chartPlatformLabels.forEach((p) => {
          const obj = {
            name: p,
            type: 'line',
            data: new Array(chartXLabels.length).fill(0),
          };
          if (!filters.channel || filters.channel.length <= 1) {
            obj.color = getPlatformColorCode(p);
          }
          chartSeriesData.push(obj);
        });

        // Append chart dataset
        res.data.rows.forEach((v) => {
          const { platform, channel, watchDate } = v;
          const platformIndex =
            chartPlatformLabelIndex[
              !filters.channel || filters.channel.length <= 1
                ? platform
                : channel
            ];
          const watchDateIndex = chartXLabelIndex[watchDate];
          if (chartSeriesData[platformIndex]) {
            chartSeriesData[platformIndex].data[watchDateIndex] +=
              v.totalSeconds;
          }
        });

        setSeriesData(chartSeriesData);
        setXLabels(chartXLabels);
        setFilters({
          ...filters,
          loading: false,
        });
      })
      .catch(() => {
        setFilters({
          ...filters,
          loading: false,
        });
      });
  }, [filters.reloadCounter]);

  return (
    <Card>
      <CardHeader
        title="Video Watched Time"
        subheader={
          <Box display="flex" alignItems="center" gap={1}>
            <Box>
              {filters.channel?.length > 1 ? 'Channel' : 'Platform'} wise number
              of video watch in
            </Box>
            <WatchTimeType
              id="watch_time_type"
              name="watch_time_type"
              defaultValue={filters.watchTimeType}
              onChange={handleWatchTimeTypeChange}
              sx={{ width: '100%' }}
              disabled={filters.loading}
            />
          </Box>
        }
        action={
          <IconButton
            title="Refresh"
            onClick={handleRefreshData}
            disabled={filters.loading}
          >
            <Iconify icon="ic:twotone-refresh" />
          </IconButton>
        }
      />

      <Box sx={{ p: 3, pb: 1 }} dir="ltr">
        <Grid container>
          <Grid item xs={6} display="flex" gap={1}>
            <Channels
              id="wssc_channel"
              name="wssc_channel"
              label="Select Channel"
              defaultValue={filters.channel}
              multiple
              onChange={handleChannelChange}
              sx={{ width: 300 }}
              disabled={filters.loading}
            />

            {filters.channel?.length > 1 && (
              <Platforms
                id="wssc_platform"
                name="wssc_platform"
                label="Select Platform"
                defaultValue={filters.platform}
                multiple
                onChange={handlePlatformChange}
                sx={{ width: 300 }}
                disabled={filters.loading}
              />
            )}
          </Grid>
          <Grid item xs={6}>
            <Stack direction="row" justifyContent="right">
              <Datepicker
                id="wssc_start_date"
                name="wssc_start_date"
                label="Start Date"
                defaultValue={filters.startDate}
                onChange={handleStartDateChange}
                sx={{ width: 200 }}
                disabled={filters.loading}
              />
              &nbsp;&nbsp;
              <Datepicker
                id="wssc_end_date"
                name="wssc_end_date"
                label="End Date"
                defaultValue={filters.endDate}
                onChange={handleEndDateChange}
                sx={{ width: 200 }}
                disabled={filters.loading}
              />
            </Stack>
          </Grid>

          {seriesData.length > 0 && (
            <Grid item xs={6}>
              <FormControlLabel
                label={`Select All ${
                  filters.channel?.length > 1 ? 'Channels' : 'Platforms'
                }`}
                control={
                  <Checkbox
                    checked={selectedAllPlatforms === 'selectAll'}
                    onChange={(e) => {
                      const { checked } = e.target;
                      setSelectedAllPlatforms(checked ? 'selectAll' : null);
                      if (checked && chartInstance) {
                        seriesData.forEach((s) => {
                          chartInstance.showSeries(s.name);
                        });
                      }
                    }}
                  />
                }
              />

              <FormControlLabel
                label={`Deselect All ${
                  filters.channel?.length > 1 ? 'Channels' : 'Platforms'
                }`}
                control={
                  <Checkbox
                    checked={selectedAllPlatforms === 'deselectAll'}
                    onChange={(e) => {
                      const { checked } = e.target;
                      setSelectedAllPlatforms(checked ? 'deselectAll' : null);
                      if (checked && chartInstance) {
                        seriesData.forEach((s) => {
                          chartInstance.hideSeries(s.name);
                        });
                      }
                    }}
                  />
                }
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <Stack sx={{ marginTop: 2 }}>
              <ReactApexChart
                key={filters.channel?.length > 1 ? filters.reloadCounter : null}
                type="line"
                series={seriesData}
                options={chartOptions}
                height={364}
              />
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </Card>
  );
};

export default WatchedSecondsChart;
